import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DONATE_URL } from "@/lib/links";
import { ArrowRight, ArrowLeft, ShieldCheck, Shield } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Markdown } from "@/components/Markdown";
import { DIVISION_ICONS } from "@/pages/Divisions";
import { api, API } from "@/lib/api";

const FALLBACK_HERO = "https://images.pexels.com/photos/7649100/pexels-photo-7649100.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600";

export default function DivisionDetail() {
  const { slug } = useParams();
  const [div, setDiv] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setDiv(null); setNotFound(false);
    api.get(`/divisions/${slug}`).then(({ data }) => setDiv(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div>
        <Navbar />
        <section className="pt-40 pb-40 bg-muted text-center px-5">
          <h1 className="font-display text-4xl uppercase">Division Not Found</h1>
          <Link to="/divisions" className="inline-flex items-center gap-2 mt-8 bg-[#D90429] text-white px-8 py-3 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200">All Divisions</Link>
        </section>
        <Footer />
      </div>
    );
  }
  if (!div) {
    return (<div><Navbar /><div className="pt-40 pb-40 text-center text-slate-400">Loading…</div><Footer /></div>);
  }

  const Icon = DIVISION_ICONS[div.icon] || Shield;
  const heroId = div.hero_image_file_id || div.image_file_id;
  const heroSrc = heroId ? `${API}/public/files/${heroId}` : FALLBACK_HERO;
  const bullets = (div.bullets || []).filter(Boolean);
  const gallery = (div.gallery || []).filter(Boolean);
  const applyHref = `/join?division=${encodeURIComponent(div.title)}`;

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 grain stars tactical-grid overflow-hidden bg-[#0A1128]">
        <img src={heroSrc} alt={div.title} className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/85 to-[#0A1128]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] to-transparent" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <Link to="/divisions" data-testid="back-to-divisions" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-[0.2em] font-semibold mb-6">
            <ArrowLeft className="w-4 h-4" /> All Divisions
          </Link>
          <div className="w-16 h-16 bg-[#D90429] flex items-center justify-center mb-6">
            <Icon className="w-8 h-8 text-white" strokeWidth={1.75} />
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="division-detail-title" className="font-display text-5xl sm:text-7xl uppercase text-white leading-[0.92] drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
            {div.title}
          </motion.h1>
          {div.tagline && <p className="text-[#F4A261] text-lg sm:text-xl mt-5 max-w-2xl">{div.tagline}</p>}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 chevron-accent opacity-80" />
      </section>

      {/* Body + bullets */}
      <section className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {div.body ? <Markdown>{div.body}</Markdown> : <p className="text-slate-600 leading-relaxed">{div.text}</p>}
          </div>
          <aside className="lg:col-span-1">
            {bullets.length > 0 && (
              <div className="bg-[#0A1128] text-white p-8 sticky top-28" data-testid="division-bullets">
                <h2 className="font-display text-2xl uppercase mb-6">What We Do</h2>
                <ul className="space-y-4">
                  {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <ShieldCheck className="w-5 h-5 text-[#D90429] shrink-0 mt-0.5" />
                      <span className="text-white/85">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="pb-24" data-testid="division-gallery">
          <div className="max-w-5xl mx-auto px-5 sm:px-8">
            <h2 className="font-display text-3xl uppercase mb-8">Gallery</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((id, i) => (
                <div key={i} className="aspect-[4/3] overflow-hidden bg-slate-100 group">
                  <img src={`${API}/public/files/${id}`} alt={`${div.title} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#0A1128] text-white py-20 grain">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <Icon className="w-10 h-10 text-[#D90429] mx-auto mb-6" strokeWidth={1.75} />
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Serve With {div.title}</h2>
          <p className="text-white/70 mt-5 max-w-xl mx-auto">Ready to stand your post? Join our newsletter and stay connected to the mission and every division.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to={applyHref} data-testid="division-apply-button" className="bg-white text-[#0A1128] px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#F4A261] transition-colors duration-200 active:scale-95">Join Our Newsletter</Link>
            <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="division-donate-button" className="inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95">Fund The Mission <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
